import axios from 'axios';
import axiosRetry from 'axios-retry';
import { history } from './history.js';

let getAuthToken = () => null;
let handleUnauthorized = () => {};

/**
 * Called from store.js once the auth slice exists.
 * Avoids a circular import between api.js and the auth slice.
 */
export function configureAuth({ getToken, onUnauthorized }) {
  getAuthToken = getToken;
  handleUnauthorized = onUnauthorized || (() => {});
}

// Legacy backend mounts business endpoints under v1/ (except /v3/user_profile).
// We default to /v1 and pass through anything starting with v3/ verbatim.
const RAW_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

export const api = axios.create({
  baseURL: `${RAW_BASE}/v1/`,
  timeout: 30000,
  // Matches legacy. The server may dispatch routes by `platform`; 'w2' is unverified.
  headers: { Accept: 'application/json', platform: 'w1' },
});

api.interceptors.request.use((config) => {
  // Endpoints under v3/ are not prefixed with v1/ — re-target to RAW_BASE.
  if (typeof config.url === 'string' && config.url.startsWith('v3/')) {
    config.baseURL = `${RAW_BASE}/`;
  }
  const token = getAuthToken() || localStorage.getItem('token');
  // Legacy expects a custom `access-token` header (see settings.sagas.js / myCourse.sagas.js).
  if (token && !config.headers['access-token']) config.headers['access-token'] = token;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Avoid bounce loops on auth pages
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const requestUrl = String(error.config?.url || '');
      const isOnboardingRequest = requestUrl.replace(/^\/+/, '') === 'onboarding';

      if (
        !error.config?.suppressUnauthorizedRedirect &&
        !path.startsWith('/signin') &&
        !path.startsWith('/signup') &&
        !isOnboardingRequest
      ) {
        localStorage.removeItem('token');
        localStorage.removeItem('login_user_id');
        handleUnauthorized();
        history.push('/signin?expired=1');
      }
    }
    return Promise.reject(error);
  },
);

axiosRetry(api, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (err) =>
    (axiosRetry.isNetworkError(err) || axiosRetry.isRetryableError(err)) &&
    ['get', 'head', 'options'].includes((err.config?.method || '').toLowerCase()),
});
